import React, { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { generatePath, Link, useLocation } from 'react-router-dom';
import moment from 'moment';
import DOMPurify from 'dompurify';
import RoomIcon from '@mui/icons-material/Room';
import { ExpandMore } from '@mui/icons-material';
import { Timeline, TimelineConnector, TimelineDot, TimelineItem, TimelineSeparator } from '@mui/lab';
import {
  Box,
  Grid,
  IconButton,
  Breadcrumbs,
  Typography,
  Tooltip,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import Linkify from 'react-linkify';
import { get } from 'lodash';
import PastRescueForm from './PastRescueForm';
import { GridRow, RowContent, RowTitle } from '../FormGrid';
import {
  calculatedLbsSource,
  getRescuedFoodTypesLabel,
  getRescuedItemsFoodTypes,
  getRescueDropoffAddress,
  getRescueEstimatedLbs,
  getRescuePickupAddress,
  getRescuePickupLocationFullName,
  isHTML,
} from '../../../../helpers/RescuesHelper';
import { formatPhoneNumber, formatRescueDistance, formatRescueDuration } from '../../../../helpers/formatters';
import { fetchRescueSizesIfNeeded } from '../../../../actions/rescueSizes';
import useActiveSite from '../../../../hooks/useActiveSite';
import {
  fetchSiteReceiversIfNeeded,
  fetchSiteRescuersIfNeeded,
  fetchSiteUsersIfNeeded,
  fetchSitesPhotos,
} from '../../../../actions/sites';
import { fetchUnitsIfNeeded } from '../../../../actions/units';
import { fetchFoodDescriptionsIfNeeded } from '../../../../actions/foodDescriptions';
import { sortArrayOfObjects, sortByDateAndTime } from '../../../../helpers/sorters';
import * as rescuesActions from '../../../../actions/rescues';
import routes from '../../../../routes';
import { hasAnyRoleInSite } from '../../../../services/auth';
import { Roles } from '../../../../models/roles';
import { fetchSystemSettingsIfNeeded } from '../../../../actions/systemSettings';
import useSystemSettings from '../../../../hooks/useSystemSettings';
import { LBS_PER_MEAL } from '../../../../models/systemSettings';
import { Colors } from '../../../../assets/theme/Colors';
import { fetchDonationSizesIfNeeded } from '../../../../actions/donationSizes';
import PhotoDialog from '../../RescueGallery/Components/Lightbox/PhotoDialog';
import GalleryGridItem from '../../RescueGallery/Components/GalleryGridItem';
import DonorLinkIcon from '../../../../components/DonorLinkIcon';
import useHasActiveFeatureFlag from '../../../../hooks/useFeatureFlags';
import { FF_DONOR_LINK } from '../../../../components/FeatureFlagWrapper';
import { fetchDonationLogs, fetchPickupSpecLogs, fetchRescueLogs } from '../../../../actions/logs';
import { getUserFullName } from '../../../../helpers/user';

const handleAddressClick = address => window.open(`https://maps.google.com/?q=${address}`, '_blank');

const addressRowSx = {
  '&:hover': {
    cursor: 'pointer',
  },
};

const summarySx = {
  fontWeight: 700,
};

const timelineItemSx = {
  '&:before': {
    display: 'none',
  },
};

const RESCUE_DETAILS = 'rescue-details';
const ACTIVITY = 'activity';

const PastRescueView = ({ rescue, isMobileView }) => {
  const dispatch = useDispatch();
  const activeSite = useActiveSite();
  const location = useLocation();
  const rescueSizes = useSelector(state => state.entities.rescueSizes);
  const siteRescuers = useSelector(state => state.entities.sites.rescuers);
  const siteUsers = useSelector(state => state.entities.sites.users);
  const foodDescriptions = useSelector(state => state.entities.foodDescriptions);
  const unitTypes = useSelector(state => state.entities.units);
  const sitePhotos = useSelector(state => state.entities.sites.photos);
  const rescuePhotos = Object.values(sitePhotos.byId).filter((photo) => photo?.rescue_id === rescue.id);
  const logsEntities = useSelector((state) => state.entities.logs);
  const rescueLogs = logsEntities.byRescueId[rescue.id] || [];
  const donationLogs = logsEntities.byDonationId[rescue.donation_id] || [];
  const donationLogsInRescueDateRange = donationLogs.filter(
    (log) =>
      moment(log.created_at).startOf('day').isSameOrAfter(moment(rescue.start_date, 'YYYY-MM-DD')) &&
      moment(log.created_at).startOf('day').isSameOrBefore(moment(rescue.date, 'YYYY-MM-DD'))
  );
  const pickupSpecLogs = logsEntities.byPickupSpecId[rescue.pickup_spec_id] || [];
  const rescueLogsByDate = [...rescueLogs, ...donationLogsInRescueDateRange, ...pickupSpecLogs].sort((a, b) =>
    sortByDateAndTime('asc', a.created_at, b.created_at)
  );
  const lbsPerMeal = useSystemSettings(LBS_PER_MEAL);
  const [weight, setWeight] = useState(null);
  const [showPhoto, setShowPhoto] = useState();
  const [expanded, setExpanded] = useState(RESCUE_DETAILS);
  const hasDonorLinkFF = useHasActiveFeatureFlag(FF_DONOR_LINK);

  const siteRescuersList = siteRescuers.bySiteId[activeSite.id] || [];
  const siteUsersList = siteUsers.bySiteId[activeSite.id] || [];
  const rescueSizesArray = rescueSizes.sorted;
  const unitTypeOther = Object.values(unitTypes.byId).find(ot => ot.name === 'Other');
  const unitTypesArray = sortArrayOfObjects(Object.values(unitTypes.byId), u => u.name);
  const foodDescriptionOther = Object.values(foodDescriptions.byId).find(ot => ot.name === 'Other');
  const foodDescriptionTypesArray = sortArrayOfObjects(Object.values(foodDescriptions.byId), u => u.name);
  const rescueSize = rescueSizes.byId[rescue.rescue_size_id];
  const estimatedLBSValue = getRescueEstimatedLbs(rescueSize, lbsPerMeal);

  // TODO: add NSD/Admin users to the list
  const cancelledByUsersList = siteUsersList.filter(user =>
    hasAnyRoleInSite(user, activeSite.id, [Roles.SiteDirector, Roles.SiteCoordinator])
  );

  useEffect(() => {
    dispatch(fetchRescueSizesIfNeeded());
    dispatch(fetchDonationSizesIfNeeded());
    dispatch(fetchSiteRescuersIfNeeded(activeSite.id));
    dispatch(fetchSiteUsersIfNeeded(activeSite.id));
    dispatch(fetchSiteReceiversIfNeeded(rescue.site_id));
    dispatch(fetchUnitsIfNeeded());
    dispatch(fetchFoodDescriptionsIfNeeded());
    dispatch(fetchSystemSettingsIfNeeded()); // used in LBSSelector
    dispatch(fetchSitesPhotos(activeSite.id, null, rescue.date, rescue.date)); // siteId, orerBy, from
    // Logs
    dispatch(fetchRescueLogs(rescue.id));
    dispatch(fetchPickupSpecLogs(rescue.pickup_spec_id));
    dispatch(fetchDonationLogs(rescue.donation_id));
  }, [dispatch]);

  const pickupAddress = getRescuePickupAddress(rescue);
  const dropoffAddress = getRescueDropoffAddress(rescue);
  const rescueCompleted = rescue?.completed;
  const foodTypesLabel = getRescuedFoodTypesLabel(rescue);
  const RescuedItemsFoodTypes = getRescuedItemsFoodTypes(rescue, foodDescriptionTypesArray);
  const [categories, setCategories] = useState(rescueCompleted ? RescuedItemsFoodTypes : foodTypesLabel);

  const formatRescueLog = (type) => {
    switch (type) {
      case 'RESCUE_CLAIM':
        return 'Claimed';
      case 'RESCUE_RELEASE':
        return 'Unclaimed';
      case 'RESCUE_UNADOPT':
        return 'Unadopted';
      case 'RESCUE_ADOPT':
        return 'Adopted';
      case 'DONATION_MODIFIED':
        return 'Modified';
      default:
        return type;
    }
  };

  const handleAccordionChange = panel => (event, isExpanded) => {
    return setExpanded(
      isExpanded ? panel : expanded === RESCUE_DETAILS ? ACTIVITY : RESCUE_DETAILS
    );
  };

  const handleRescueUpdate = async (rescueId, data) => {
    await dispatch(rescuesActions.updatePastRescue(rescueId, data));

    if (data.photos?.length) {
      dispatch(fetchSitesPhotos(activeSite.id, null, rescue.date, rescue.date));
    }
  };

  const handlePrevPhotoChange = () => {
    const prevPhoto = showPhoto - 1;

    if (prevPhoto >= 0) {
      setShowPhoto(prevPhoto);
    }
  };

  const handleNextPhotoChange = () => {
    const nextPhoto = showPhoto + 1;

    if (nextPhoto < rescuePhotos.length) {
      setShowPhoto(nextPhoto);
    }
  };

  const renderBreadcrumbs = () => {
    if (location.state?.prevPath === routes.rescuesSchedule) {
      return (
        <Breadcrumbs aria-label="Breadcrumbs">
          <Link color="inherit" to={{ pathname: routes.rescuesSchedule, state: location.state }}>
            Schedule
          </Link>
          <Typography color="textPrimary">{rescue.id}</Typography>
        </Breadcrumbs>
      );
    }

    if (location.state?.prevPath === generatePath(routes.dashboardRescuer)) {
      return (
        <Breadcrumbs aria-label="Breadcrumbs">
          <Link color="inherit" to={generatePath(routes.dashboardRescuer)}>
            Dashboard
          </Link>
          <Typography color="textPrimary">{rescue.id}</Typography>
        </Breadcrumbs>
      );
    }

    return (
      <Breadcrumbs aria-label="Breadcrumbs">
        <Link color="inherit" to={{ pathname: routes.pastrescues, state: location.state }}>
          Past Rescues
        </Link>

        <Typography color="textPrimary">{rescue.id}</Typography>
      </Breadcrumbs>
    );
  };

  if (unitTypes.inflight || foodDescriptions.inflight || siteRescuers.inflight) {
    return 'loading...';
  }

  // @todo: figure out why sometimes food_types are string...
  if (typeof rescue.food_types === 'string') {
    rescue.food_types = rescue.food_types.split(',');
  }

  return (
    <>
      <PhotoDialog
        disablePrevButton={showPhoto === 0}
        disableNextButton={showPhoto === rescuePhotos.length - 1}
        fullScreen={isMobileView}
        rescuePhotoData={rescuePhotos[showPhoto]}
        handlePrevPhoto={handlePrevPhotoChange}
        handleNextPhoto={handleNextPhotoChange}
        open={!isNaN(showPhoto)}
        handleClose={() => setShowPhoto(false)}
      />

      {renderBreadcrumbs()}
      <Box sx={{ overflow: 'hidden' }}>
        <Grid container spacing={3}>
          <Grid
            item
            sm={12}
            md={4}
            sx={(theme) => ({
              [theme.breakpoints.up('md')]: {
                flexBasis: 'max(33.33333%, 400px)',
                maxWidth: 'max(33.33333%, 400px)',
              },
            })}
          >
            <Accordion
              elevation={25}
              expanded={expanded === RESCUE_DETAILS}
              onChange={handleAccordionChange(RESCUE_DETAILS)}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="body1" sx={summarySx}>
                  Rescue Details
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Grid
                  container
                  direction="column"
                  justifyContent="flex-start"
                  alignItems="stretch"
                  spacing={3}
                  sx={{ position: 'relative' }}
                >
                  <GridRow>
                    <RowTitle>Date:</RowTitle>
                    <RowContent>{moment(rescue.date, 'YYYY-MM-DD').format('dddd, MMMM Do')}</RowContent>
                  </GridRow>

                  <GridRow>
                    <RowTitle>From:</RowTitle>
                    <RowContent>{getRescuePickupLocationFullName(rescue)}</RowContent>
                  </GridRow>

                  <GridRow>
                    <RowTitle>To:</RowTitle>
                    <RowContent>{rescue.receiver}</RowContent>
                  </GridRow>

                  <GridRow>
                    <RowTitle>
                      Weight:
                    </RowTitle>

                    <RowContent>
                      {weight !== null ? (
                        <span style={{ color: Colors.green.main, fontWeight: 'normal' }}>{weight} lbs</span>
                      ) : (
                        '-'
                      )}
                    </RowContent>
                  </GridRow>

                  <GridRow>
                    <RowTitle>
                      Weight Source:
                    </RowTitle>

                    <RowContent>
                      {calculatedLbsSource(rescue.calculated_lbs_source)
                        ? calculatedLbsSource(rescue.calculated_lbs_source)
                        : '-'}
                    </RowContent>
                  </GridRow>

                  <GridRow>
                    <RowTitle>Water Offset:</RowTitle>

                    <RowContent>
                      {rescue?.water != null
                      ? `${rescue.water} gallons` : '-'}
                    </RowContent>
                  </GridRow>

                  <GridRow>
                    <RowTitle>CO2eq Offset:</RowTitle>

                    <RowContent>
                      {rescue?.co2eq != null
                        ? `${rescue.co2eq} lbs` : '-'}
                    </RowContent>
                  </GridRow>

                  <GridRow>
                    <RowTitle>Distance / Duration:</RowTitle>

                    <RowContent>
                      {`${formatRescueDistance(rescue.distance, ' miles')} / ${formatRescueDuration(
                        rescue.duration,
                        false
                      )}`}
                    </RowContent>
                  </GridRow>

                  <GridRow hidden={!rescue.food_types}>
                    <RowTitle>Food Type(s):</RowTitle>

                    <RowContent>{categories}</RowContent>
                  </GridRow>

                  <GridRow hidden={!rescue.rescue_size}>
                    <RowTitle>Rescue Vehicle Size:</RowTitle>
                    <RowContent>{rescue.rescue_size}</RowContent>
                  </GridRow>

                  <GridRow>
                    <RowTitle>
                      <Grid container direction="row" justifyContent="flex-start" alignItems="center">
                        <Grid item>Pickup Address:</Grid>
                        <Grid item>
                          <Box ml={4}>
                            <IconButton size="small" aria-label="delete" onClick={() => handleAddressClick(pickupAddress)}>
                              <RoomIcon htmlColor="#000" fontSize="small" />
                            </IconButton>
                          </Box>
                        </Grid>
                      </Grid>
                    </RowTitle>
                    <RowContent>
                      <span style={addressRowSx} onClick={() => handleAddressClick(pickupAddress)}>
                        {pickupAddress}
                      </span>
                    </RowContent>
                  </GridRow>

                  <GridRow>
                    <RowTitle>Pickup Contact:</RowTitle>
                    {rescue.pickup_contact && <RowContent>Name: {rescue.pickup_contact}</RowContent>}
                    {rescue.pickup_contact_phone && (
                      <RowContent>
                        <span>Phone: </span>
                        <a href={`tel:${rescue.pickup_contact_phone}`}>{formatPhoneNumber(rescue.pickup_contact_phone)}</a>
                        {rescue.pickup_contact_phone_ext && <span>{` (ext: ${rescue.pickup_contact_phone_ext})`}</span>}
                      </RowContent>
                    )}
                    {rescue.pickup_contact_email && (
                      <RowContent>
                        Email: <a href={`mailto:${rescue.pickup_contact_email}`}>{rescue.pickup_contact_email}</a>
                      </RowContent>
                    )}
                  </GridRow>

                  <GridRow hidden={!rescue.pickup_notes}>
                    <RowTitle>Pickup Notes:</RowTitle>
                    <RowContent>
                      {isHTML(rescue?.pickup_notes) ? (
                        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(rescue?.pickup_notes) }} />
                      ) : (
                        <Linkify
                          componentDecorator={(decoratedHref, decoratedText, key) => (
                            <a target="blank" href={decoratedHref} key={key}>
                              {decoratedText}
                            </a>
                        )}
                        >
                          <span style={{ whiteSpace: 'pre-wrap' }}>{rescue?.pickup_notes}</span>
                        </Linkify>
                      )}
                    </RowContent>
                  </GridRow>

                  <GridRow>
                    <RowTitle>
                      <Grid container direction="row" justifyContent="flex-start" alignItems="center">
                        <Grid item>Dropoff Address:</Grid>
                        <Grid item>
                          <Box ml={4}>
                            <IconButton size="small" aria-label="delete" onClick={() => handleAddressClick(dropoffAddress)}>
                              <RoomIcon htmlColor="#000" fontSize="small" />
                            </IconButton>
                          </Box>
                        </Grid>
                      </Grid>
                    </RowTitle>
                    <RowContent>
                      <span style={addressRowSx} onClick={() => handleAddressClick(dropoffAddress)}>
                        {dropoffAddress}
                      </span>
                    </RowContent>
                  </GridRow>

                  <GridRow>
                    <RowTitle>Dropoff Contact:</RowTitle>
                    {rescue.dropoff_contact && <RowContent>Name: {rescue.dropoff_contact}</RowContent>}
                    {rescue.dropoff_contact_phone && (
                      <RowContent>
                        <span>Phone: </span>
                        <a href={`tel:${rescue.dropoff_contact_phone}`}>
                          {formatPhoneNumber(rescue.dropoff_contact_phone)}
                        </a>
                        {rescue.dropoff_contact_phone_ext && <span>{` (ext: ${rescue.dropoff_contact_phone_ext})`}</span>}
                      </RowContent>
                    )}
                    {rescue.dropoff_contact_email && (
                      <RowContent>
                        Email: <a href={`mailto:${rescue.dropoff_contact_email}`}>{rescue.dropoff_contact_email}</a>
                      </RowContent>
                    )}
                  </GridRow>

                  <GridRow hidden={!rescue.dropoff_notes}>
                    <RowTitle>Dropoff Notes:</RowTitle>
                    <RowContent>
                      {isHTML(rescue?.dropoff_notes) ? (
                        <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(rescue?.dropoff_notes) }} />
                      ) : (
                        <Linkify
                          componentDecorator={(decoratedHref, decoratedText, key) => (
                            <a target="blank" href={decoratedHref} key={key}>
                              {decoratedText}
                            </a>
                        )}
                        >
                          <span style={{ whiteSpace: 'pre-wrap' }}>{rescue?.dropoff_notes}</span>
                        </Linkify>
                      )}
                    </RowContent>
                  </GridRow>

                  <GridRow hidden={!rescuePhotos?.length}>
                    <RowTitle>Rescue Photo(s):</RowTitle>
                    <RowContent>
                      <Grid container spacing={1}>
                        {rescuePhotos.map((photo, index) => (
                          <Grid key={photo.id} item xs={4} md={6}>
                            <GalleryGridItem handleDialogOpen={() => setShowPhoto(index)} tile={photo}>
                              <Box height={200} />
                            </GalleryGridItem>
                          </Grid>
                        ))}
                      </Grid>
                    </RowContent>
                  </GridRow>

                  {(hasDonorLinkFF && !!rescue.location_donor_link_active) && (
                    <Tooltip arrow placement="top" title="This is a Donor Link rescue">
                      <span style={{ top: 16, right: 16, position: 'absolute' }}>
                        <DonorLinkIcon size={24} isActive={true} />
                      </span>
                    </Tooltip>
                  )}
                </Grid>
              </AccordionDetails>
            </Accordion>

            <Accordion
              elevation={25}
              style={{ marginTop: 8 }}
              expanded={expanded === ACTIVITY}
              onChange={handleAccordionChange(ACTIVITY)}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="body1" sx={summarySx}>
                  Activity
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Timeline>
                  {rescueLogsByDate.map((log) => {
                    const user = siteUsersList.find((u) => u.id === log.user_id);
                    const userFullName = getUserFullName(user);

                    return (
                      <TimelineItem key={log.id} sx={timelineItemSx}>
                        <TimelineSeparator>
                          <TimelineDot />
                          <TimelineConnector />
                        </TimelineSeparator>
                        <Box ml={1}>
                          <Typography variant="body2">
                            {formatRescueLog(log.action_type)} - {userFullName}
                          </Typography>
                          <Typography variant="body2">{moment(log.created_at).format('M/DD/YY hh:mmA')}</Typography>
                        </Box>
                      </TimelineItem>
                    );
                  })}

                  <TimelineItem sx={timelineItemSx}>
                    <TimelineSeparator>
                      <TimelineDot />
                    </TimelineSeparator>
                    <Box ml={1}>
                      <Typography variant="body2">Rescue Created</Typography>
                      <Typography variant="body2">{moment(rescue.start_date, 'YYYY-MM-DD').format('M/DD/YY')}</Typography>
                    </Box>
                  </TimelineItem>
                </Timeline>
              </AccordionDetails>
            </Accordion>
          </Grid>

          <Grid item sm={12} md>
            <PastRescueForm
              rescue={rescue}
              estimatedLBSValue={estimatedLBSValue}
              isMobileView={isMobileView}
              rescueSizes={rescueSizesArray}
              rescuersList={siteRescuersList}
              cancelledByUsersList={cancelledByUsersList}
              foodDescriptions={foodDescriptionTypesArray}
              unitTypes={unitTypesArray}
              foodDescriptionOtherId={get(foodDescriptionOther, 'id', null)}
              unitTypeOtherId={get(unitTypeOther, 'id', null)}
              updateRescue={handleRescueUpdate}
              setWeight={setWeight}
              setCategories={setCategories}
            />
          </Grid>
        </Grid>
      </Box>
    </>
  );
};

export default PastRescueView;
